import React, { useCallback, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { Icon, FileUpload, LabeledBevestigingInput, useFeature, useRequestInit } from "adviesbox-shared";
import classNames from "classnames";
import InteractiveStreetView from "../components/InteractiveStreetView";
import MapsImage from "../../shared/components/mapsimage/MapImage";
import { SoortFoto, FotoWoningType } from "../infra/woning-schema";
import classes from "../woning-scherm.module.scss";
import {
  calculateSquareMask as utilCalculateSquareMask,
  performStreetViewUpload as utilPerformStreetViewUpload
} from "./foto-woning-utils";
import { AdresType } from "../../shared/types";
import { uploadFotoVanWoningApi, getBlobUrlApi } from "../woning-api/woning-api";

type FotoWoningProps = {
  modelPrefix: string;
  fotoWoning: FotoWoningType;
  adres: AdresType;
  formik: any;
  addressChangeKey: number;
  handleAddressChanged: () => void;
};
const FotoWoning: React.FC<FotoWoningProps> = ({
  modelPrefix,
  fotoWoning,
  adres,
  formik,
  addressChangeKey,
  handleAddressChanged
}) => {
  const featureAdviesFoto = useFeature("FeatureAdviesFoto");
  const model = `${modelPrefix}.fotoWoning`;
  const isHuidigeWoning = model.startsWith("huidigeWoningen");
  const [isUploadingStreetView, setIsUploadingStreetView] = useState(false);
  const [imageUpdateKey, setImageUpdateKey] = useState(0); // Force re-render trigger
  const [maskClipPath, setMaskClipPath] = useState(
    "polygon(0% 0%, 0% 100%, 25% 100%, 25% 25%, 75% 25%, 75% 75%, 25% 75%, 25% 100%, 100% 100%, 100% 0%)"
  );
  const { settings, params, user } = useRequestInit<{ vestiging: string; adviesdossier: string }>();

  // Function that actually performs the upload
  const performStreetViewUpload = useCallback(
    async (streetViewUrl: string): Promise<void> => {
      /* istanbul ignore next */
      await utilPerformStreetViewUpload({
        streetViewUrl,
        settings,
        user,
        vestiging: params.vestiging,
        adviesdossier: params.adviesdossier,
        modelPrefix,
        fotoWoning,
        formik,
        setIsUploadingStreetView,
        setImageUpdateKey
      });
    },
    [settings, user, params.vestiging, params.adviesdossier, modelPrefix, fotoWoning, formik, setImageUpdateKey]
  );

  // Handle manual snapshot button click
  // Save the current position to Formik when snapshot is taken
  /* istanbul ignore next */
  const handleStreetViewSnapshot = useCallback(
    (streetViewUrl: string): void => {
      // Save the position to Formik when taking snapshot
      const urlWithoutProtocol = streetViewUrl.replace("https://", "");
      formik.setFieldValue(`${model}.streetViewUrl`, urlWithoutProtocol);
      performStreetViewUpload(streetViewUrl).catch(() => {});
    },
    [performStreetViewUpload, formik, model]
  );

  // Function to calculate square mask based on image dimensions
  // (If you want to keep this logic here, otherwise pass it as prop)
  const calculateSquareMask = useCallback(
    (img: HTMLImageElement) => {
      const clipPath = utilCalculateSquareMask(img);
      setMaskClipPath(clipPath);
    },
    [setMaskClipPath]
  );

  // Recalculate mask when image updates
  useEffect(() => {
    if (fotoWoning.locatieFoto) {
      // Find the image element and recalculate mask if it exists
      const img = document.querySelector('[data-testid="blob-image"]') as HTMLImageElement;
      if (img && img.complete) {
        /* istanbul ignore next */
        calculateSquareMask(img);
      }
    }
  }, [imageUpdateKey, fotoWoning.locatieFoto, calculateSquareMask]);

  return (
    <>
      <LabeledBevestigingInput caption="Foto opnemen in rapportage" name={`${model}.fotoGebruikRapportage`} />
      {fotoWoning.fotoGebruikRapportage && (
        <>
          <div className="my-3">
            <div className="d-flex justify-content-between align-items-center gap-2 mt-2 mx-5">
              <div className="d-flex gap-2">
                <Button
                  data-testid="btn-eigenfoto"
                  variant={fotoWoning.soortFoto === SoortFoto.Eigen ? "primary" : "outline-primary"}
                  onClick={/* istanbul ignore next */ () => formik.setFieldValue(`${model}.soortFoto`, SoortFoto.Eigen)}
                >
                  Foto uploaden
                </Button>

                <Button
                  data-testid="btn-google-maps"
                  variant={fotoWoning.soortFoto === SoortFoto.GoogleMaps ? "primary" : "outline-primary"}
                  onClick={() => formik.setFieldValue(`${model}.soortFoto`, SoortFoto.GoogleMaps)}
                >
                  Google maps
                </Button>
              </div>
              {fotoWoning.locatieFoto && fotoWoning.soortFoto === SoortFoto.Eigen && (
                <Button
                  data-testid="btn-verwijder-woning-foto"
                  variant="outline-secondary"
                  size="sm"
                  onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>): void => {
                    event.stopPropagation();
                    formik.setFieldValue(`${model}.locatieFoto`, null);
                  }}
                >
                  <Icon name="resettodefault" alt="Verwijderen" />
                </Button>
              )}
            </div>
          </div>

          {/* Show Street View for Google Maps or regular image for uploaded photos */}
          {fotoWoning.soortFoto === SoortFoto.GoogleMaps ||
          (fotoWoning.soortFoto === SoortFoto.Eigen && fotoWoning.locatieFoto?.includes("blob")) ? (
            <div className={classNames("mb-3")}>
              {featureAdviesFoto && fotoWoning.soortFoto === SoortFoto.GoogleMaps && (
                // Interactive Street View for Google Maps
                <div style={{ position: "relative", width: "720px", height: "400px", margin: "0 auto" }}>
                  <InteractiveStreetView
                    onPositionChange={/* istanbul ignore next */ (): void => {}}
                    onSnapshot={handleStreetViewSnapshot}
                    onAddressChanged={handleAddressChanged}
                    addressChangeKey={addressChangeKey}
                    initialUrl={
                      fotoWoning.streetViewUrl
                        ? "https://" + fotoWoning.streetViewUrl
                        : fotoWoning.locatieFoto && !fotoWoning.locatieFoto.includes("blob")
                        ? "https://" + fotoWoning.locatieFoto
                        : undefined
                    }
                    className={classes.img}
                    isHuidigeWoning={isHuidigeWoning}
                  />
                  {/* istanbul ignore next */}
                  {isUploadingStreetView && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        background: "rgba(0,0,0,0.7)",
                        color: "white",
                        padding: "10px 20px",
                        borderRadius: "8px",
                        textAlign: "center",
                        zIndex: 2000
                      }}
                    >
                      <div className="spinner-border spinner-border-sm mb-2" role="status">
                        <span className="sr-only">Uploading...</span>
                      </div>
                      <div>
                        <small>Afbeelding uploaden...</small>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* istanbul ignore next */}
              {!featureAdviesFoto && fotoWoning.soortFoto === SoortFoto.GoogleMaps && <MapsImage address={adres} />}

              {fotoWoning.soortFoto === SoortFoto.Eigen && (
                // Regular image display for uploaded photos
                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "720px",
                      height: "360px",
                      margin: "0 auto"
                    }}
                  >
                    <img
                      data-testid="blob-image"
                      className={classNames("maps-image", classes.img)}
                      src={/* istanbul ignore next */ fotoWoning.locatieFoto ?? ""}
                      alt="Woning afbeelding"
                      key={`woning-image-${imageUpdateKey}`}
                      onLoad={e => /* istanbul ignore next */ calculateSquareMask(e.target as HTMLImageElement)}
                      style={{
                        display: "block",
                        width: "720px",
                        height: "360px",
                        objectFit: "cover"
                      }}
                    />
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      background: "rgba(255, 255, 255, 0.8)",
                      clipPath: maskClipPath,
                      pointerEvents: "none"
                    }}
                  />
                </div>
              )}
            </div>
          ) : null}

          {fotoWoning.soortFoto === SoortFoto.Eigen && !fotoWoning.locatieFoto?.includes("blob") && (
            <div className={classes.img}>
              <FileUpload
                name={`${model}.locatieFoto`}
                variant="Afbeeldingen"
                updateRef={(f: File[]) => {
                  (async (): Promise<void> => {
                    // Don't proceed if no files are provided
                    if (!f || f.length === 0) {
                      return;
                    }
                    const file = f[0];
                    const formData = new FormData();
                    formData.append("fotoWoningBestand", file);
                    const success = await uploadFotoVanWoningApi(
                      settings,
                      user,
                      params.vestiging,
                      params.adviesdossier,
                      formData,
                      formik
                    );
                    if (!!success) {
                      const locatieFoto = await getBlobUrlApi(
                        settings,
                        user,
                        params.vestiging,
                        params.adviesdossier,
                        isHuidigeWoning
                      );
                      formik.setFieldValue(`${model}.locatieFoto`, locatieFoto);
                    }
                  })().catch(() => {});
                }}
                multipleFiles={false}
                hideFormats
                allowExtensions={[".png", ".jpg", ".jpeg"]}
                buttonText="Kies een bestand"
                title={
                  <>
                    <div className={classes.bold_margin}>Sleep uw foto in het vak</div>
                    <div className={classes.italic_margin}>Ondersteunde formaten: JPG, PNG</div>
                  </>
                }
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default FotoWoning;
